import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import cn from 'classnames';
import FullViewportContainer from '../FullViewportContainer';
import Navigation from './Navigation';
import Pips from './Pips';
import Swiper from '../Scrollers/MaybeSwiper';
import connect from './connectCarousel';
import style from './styleCarousel';

const renderFullViewport = (children, anchorId) => (
  <FullViewportContainer id={anchorId}>
    {children}
  </FullViewportContainer>
);

class Carousel extends PureComponent {
  componentDidUpdate(prevProps) {
    if (this.swiper) {
      this.updateSwiper(prevProps);
    }
  }

  onCarouselPaused = () => {
    if (!this.ignoreCarouselPausedEvents) {
      const { onCarouselPaused } = this.props;
      onCarouselPaused();
    }
  };

  onSlideChange = () => {
    const { onSlideChange } = this.props;
    const { realIndex } = this.swiper;
    onSlideChange(realIndex);
  };

  setSwiperRef = swiper => {
    this.swiper = swiper;
    if (this.swiper) {
      const { isPaused } = this.props;
      this.swiper.on('realIndexChange', this.onSlideChange);
      this.swiper.on('autoplayStop', this.onCarouselPaused);
      if (isPaused) {
        if (this.swiper.autoplay.running) {
          this.stopAutoplay();
        } else {
          this.swiper.params.autoplay.enabled = false;
        }
      }
    }
  };

  stopAutoplay() {
    this.ignoreCarouselPausedEvents = true;
    if (this.swiper.autoplay) {
      this.swiper.autoplay.stop();
    }
    this.ignoreCarouselPausedEvents = false;
  }

  updateSwiper(prevProps) {
    const { isPaused, slideIndex } = this.props;
    if (slideIndex !== this.swiper.realIndex) {
      this.swiper.slideTo(slideIndex);
    }

    const wasPaused = prevProps.isPaused;
    if (wasPaused !== isPaused) {
      if (isPaused) {
        this.stopAutoplay();
      } else {
        this.swiper.autoplay.start();
      }
    }
  }

  renderBottom() {
    const {
      carouselId, classes, isCarousel, edgeToEdge,
      navigationClassName, navItems, contentType,
      hidePipsOnMobile,
    } = this.props;
    const navClassNames = cn(
      navigationClassName,
      classes.navItems,
      edgeToEdge && classes.edgeToEdgeBottom,
      contentType,
    );
    return (
      <>
        <Navigation className={navClassNames} edgeToEdge={edgeToEdge}>
          {navItems}
        </Navigation>
        {isCarousel && (
          <Pips
            className={cn(classes.pips, contentType, hidePipsOnMobile && 'hidePipsOnMobile')}
            carouselId={carouselId}
            edgeToEdge={edgeToEdge}
          />
        )}
      </>
    );
  }

  renderSwiper() {
    const {
      children, classes, slideDuration, slideIndex,
    } = this.props;
    // note: classes.slide has multiple classNames because this component uses some dynamic styles
    const slideClassName = classes.slide.split(' ')[0];
    return (
      <Swiper
        autoplay={slideDuration}
        classNames={{
          wrapper: classes.slides,
        }}
        initialIndex={slideIndex}
        longSwipesRatio={0.25} // having to drag 50% feels too far for a carousel this large
        prevNextButtons={false}
        slideClassName={slideClassName}
        swiperRef={this.setSwiperRef}
      >
        {React.Children.map(children, child => React.cloneElement(
          child,
          { className: cn(child.props.className, classes.slide) },
        ))}
      </Swiper>
    );
  }

  render() {
    const {
      classes, className, edgeToEdge, isDark, overlay, padBottom, anchorId,
    } = this.props;
    const wrapperClassName = cn(
      className,
      classes.wrapper,
      edgeToEdge && classes.edgeToEdgeWrapper,
      isDark.mobile && classes.darkWrapper,
      !isDark.mobile && classes.lightWrapper,
      !isDark.mobile && isDark.desktop && classes.desktopDarkWrapper,
      isDark.mobile && !isDark.desktop && classes.desktopLightWrapper,
      !padBottom && 'noPadding',
    );
    const children = (
      <div className={wrapperClassName}>
        <div className={classes.slidesWrapper}>
          {this.renderSwiper()}
          {overlay}
          {this.renderBottom()}
        </div>
      </div>
    );

    if (edgeToEdge) {
      return renderFullViewport(children, anchorId);
    }

    return (
      <div id={anchorId}>
        {children}
      </div>
    );
  }
}

Carousel.propTypes = {
  carouselId: PropTypes.number.isRequired,
  children: PropTypes.arrayOf(PropTypes.element),
  className: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  edgeToEdge: PropTypes.bool,
  isCarousel: PropTypes.bool.isRequired,
  isDark: PropTypes.shape({
    desktop: PropTypes.bool.isRequired,
    mobile: PropTypes.bool.isRequired,
  }).isRequired,
  isPaused: PropTypes.bool.isRequired,
  navigationClassName: PropTypes.string,
  navItems: PropTypes.arrayOf(PropTypes.element),
  onCarouselPaused: PropTypes.func.isRequired,
  onSlideChange: PropTypes.func.isRequired,
  overlay: PropTypes.element,
  padBottom: PropTypes.bool,
  hidePipsOnMobile: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types -- used in styleCarousel.js
  slideCount: PropTypes.number.isRequired,
  slideDuration: PropTypes.number.isRequired,
  slideIndex: PropTypes.number.isRequired,
  contentType: PropTypes.string,
  anchorId: PropTypes.string,
};

Carousel.defaultProps = {
  children: [],
  className: undefined,
  edgeToEdge: true,
  navigationClassName: undefined,
  navItems: undefined,
  overlay: undefined,
  padBottom: true,
  contentType: undefined,
  anchorId: undefined,
  hidePipsOnMobile: false,
};

export default compose(connect, style)(Carousel);
